<template>
  <div style="text-align:left;">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>

    <div class="column-right">
      <div v-if="admin_datos && admin_datos.superadmin" class="main-container">
        <div class="container-fluid mt-4">
          <div class="d-flex align-items-center mb-3">
            <h3 class="mb-0">Grabaciones</h3>
            <span class="ml-3 text-muted" v-if="recordings.length">{{ recordings.length }} resultados</span>
          </div>

          <div class="card mb-3 shadow-sm">
            <div class="card-body">
              <div class="form-row align-items-end">
                <div class="col-md-6">
                  <label for="searchTerm">Buscar por profesional, solicitud o call SID</label>
                  <input
                    id="searchTerm"
                    v-model="searchTerm"
                    class="form-control"
                    type="text"
                    placeholder="Nombre, correo o SID"
                    @keyup.enter="fetchRecordings"
                  />
                </div>
                <div class="col-md-3">
                  <label for="limit">Límite</label>
                  <input
                    id="limit"
                    v-model.number="limit"
                    class="form-control"
                    type="number"
                    min="1"
                    max="200"
                  />
                </div>
                <div class="col-md-3 text-right">
                  <button
                    class="btn btn-primary mt-2"
                    :disabled="loading"
                    @click="fetchRecordings"
                  >
                    <b-icon icon="search" />
                    {{ loading ? 'Buscando...' : 'Buscar' }}
                  </button>
                </div>
              </div>
              <div v-if="error" class="alert alert-danger mt-3">{{ error }}</div>
            </div>
          </div>

          <div class="card shadow-sm">
            <div class="card-body p-0">
              <div v-if="loading" class="text-center py-4">Cargando grabaciones...</div>
              <div v-else-if="!recordings.length" class="text-center py-4 text-muted">
                No se encontraron grabaciones.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Profesional</th>
                      <th>Solicitud</th>
                      <th>Estado</th>
                      <th>Duración</th>
                      <th>Transcripción</th>
                      <th>Creada</th>
                      <th class="text-right">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="recording in recordings" :key="recording.id">
                      <td>{{ recording.id }}</td>
                      <td>
                        <div class="font-weight-bold">{{ recording.profesional_nombre }}</div>
                        <small class="text-muted">Call SID: {{ recording.call_sid }}</small>
                      </td>
                      <td>
                        <router-link
                          v-if="recording.solicitud_id"
                          class="solicitud-link font-weight-bold"
                          :to="`/SolicitudDetalleAdmin/${recording.solicitud_id}`"
                        >
                          #{{ recording.solicitud_id }}
                        </router-link>
                        <div v-else class="font-weight-bold">Sin solicitud</div>
                        <small class="text-muted">SID: {{ recording.recording_sid || '—' }}</small>
                      </td>
                      <td>
                        <span class="badge" :class="statusBadge(recording)">
                          {{ statusLabel(recording) }}
                        </span>
                        <div v-if="recording.error_message" class="text-danger small mt-1">
                          {{ recording.error_message }}
                        </div>
                      </td>
                      <td>{{ formatDuration(recording.duration) }}</td>
                      <td>
                        <div class="text-capitalize">{{ recording.transcription_status || '—' }}</div>
                        <small class="text-muted">
                          {{ recording.transcription_provider || 'Sin proveedor' }}
                        </small>
                      </td>
                      <td>
                        <div>{{ formatDate(recording.created_at) }}</div>
                        <small class="text-muted">{{ formatDate(recording.transcription_completed_at) }}</small>
                      </td>
                      <td class="text-right">
                        <div class="action-buttons">
                          <button
                            class="btn btn-outline-primary"
                            :disabled="!recording.has_audio || fetchingUrlId === recording.id"
                            @click="playRecording(recording)"
                          >
                            <b-icon icon="play" />
                            {{ fetchingUrlId === recording.id ? 'Cargando...' : 'Reproducir' }}
                          </button>
                        </div>
                        <div v-if="recording.showPlayer" class="mt-2">
                          <audio
                            :ref="`audio-${recording.id}`"
                            :src="recording.url"
                            controls
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import SideNav from '@/components/SideNav.vue'
import TopNav from '@/components/TopNav.vue'

export default {
  name: 'Grabaciones',
  components: {
    SideNav,
    TopNav
  },
  data () {
    return {
      searchTerm: '',
      limit: 50,
      recordings: [],
      loading: false,
      fetchingUrlId: null,
      error: ''
    }
  },
  computed: {
    ...mapGetters(['admin_datos'])
  },
  methods: {
    fetchRecordings () {
      if (!this.admin_datos || !this.admin_datos.superadmin) {
        return
      }

      this.loading = true
      this.error = ''
      axios.get(window.hostname + 'api/admin/recordings', {
        params: { search: this.searchTerm, limit: this.limit }
      })
        .then(resp => {
          const recordings = Array.isArray(resp.data.recordings) ? resp.data.recordings : []
          this.recordings = recordings.map(recording => ({
            ...recording,
            url: null,
            urlFetchedAt: null,
            showPlayer: false
          }))
        })
        .catch(err => {
          console.error(err)
          this.error = 'No se pudieron cargar las grabaciones.'
        })
        .finally(() => {
          this.loading = false
        })
    },
    playRecording (recording) {
      if (!recording.has_audio || this.fetchingUrlId === recording.id) {
        return
      }

      this.recordings.forEach(item => {
        if (item.id !== recording.id && item.showPlayer) {
          this.$set(item, 'showPlayer', false)
        }
      })

      const urlFresh = recording.url && recording.urlFetchedAt
        ? Date.now() - recording.urlFetchedAt < 14 * 60 * 1000
        : false

      if (urlFresh) {
        this.$set(recording, 'showPlayer', true)
        this.$nextTick(() => this.tryPlay(recording.id))
        return
      }

      this.fetchingUrlId = recording.id
      axios.get(window.hostname + `api/admin/recordings/${recording.id}/url`)
        .then(resp => {
          const url = resp.data && resp.data.url ? resp.data.url : null
          if (!url) {
            throw new Error('No se recibió URL de grabación.')
          }
          this.$set(recording, 'url', url)
          this.$set(recording, 'urlFetchedAt', Date.now())
          this.$set(recording, 'showPlayer', true)
          this.$nextTick(() => this.tryPlay(recording.id))
        })
        .catch(err => {
          console.error(err)
          this.$notify({
            group: 'foos',
            title: 'Error',
            text: 'No se pudo obtener la grabación.',
            type: 'error'
          })
        })
        .finally(() => {
          this.fetchingUrlId = null
        })
    },
    tryPlay (recordingId) {
      const ref = this.$refs[`audio-${recordingId}`]
      const audio = Array.isArray(ref) ? ref[0] : ref
      if (audio && audio.play) {
        audio.play().catch(() => {})
      }
    },
    formatDuration (seconds) {
      if (!seconds && seconds !== 0) {
        return '—'
      }
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },
    formatDate (dateValue) {
      if (!dateValue) {
        return '—'
      }
      const date = new Date(dateValue)
      if (Number.isNaN(date.getTime())) {
        return '—'
      }
      return date.toLocaleString('es-CL')
    },
    statusLabel (recording) {
      const status = (recording.status || '').toLowerCase()
      const labels = {
        pending: 'Pendiente',
        uploaded: 'Guardada',
        download_failed: 'Descarga fallida',
        upload_failed: 'Error al subir'
      }
      return labels[status] || status || '—'
    },
    statusBadge (recording) {
      const status = (recording.status || '').toLowerCase()
      const badges = {
        pending: 'badge-warning',
        uploaded: 'badge-success',
        download_failed: 'badge-danger',
        upload_failed: 'badge-danger'
      }
      return badges[status] || 'badge-secondary'
    },
    redirectIfNotSuperadmin () {
      if (this.admin_datos && !this.admin_datos.superadmin) {
        this.$router.push('/')
      }
    }
  },
  mounted () {
    if (this.admin_datos && this.admin_datos.superadmin) {
      this.fetchRecordings()
    } else {
      this.redirectIfNotSuperadmin()
    }
  },
  watch: {
    admin_datos: {
      handler (val, oldVal) {
        if (val && val.superadmin && (!oldVal || !oldVal.superadmin) && !this.recordings.length) {
          this.fetchRecordings()
        } else if (val && !val.superadmin) {
          this.redirectIfNotSuperadmin()
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.column-right{
  background-color: rgb(242, 247, 251);
  padding-top:80px;
  padding-left:260px;
  width:100%;
}

.main-container{
  background-color: white;
  margin-top:10px;
  padding:20px 20px 10px 20px;
}

.action-buttons {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.solicitud-link {
  display: inline-block;
  color: #007bff;
  text-decoration: none;
}

.solicitud-link:hover {
  text-decoration: underline;
}

@media (max-width: 800px) {
 .column-right{
  padding-left:0px;
 }
}
</style>
